import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { createLayer, createProject, createProjectFromRecommendation, createStarterProject, touchProject, } from '@/domain/mattress/factories';
import { computeTotalHeightMm, reindexLayers } from '@/domain/mattress/rules';
import { materialRegistry } from '@/domain/materials/registry';
import { DIMENSION_PRESETS } from '@/domain/mattress/types';
/** Mapeia categoria de material para role construtivo padrão da camada */
export function defaultRoleForCategory(cat) {
    switch (cat) {
        case 'core':
            return 'core';
        case 'transition':
            return 'transition';
        case 'comfort':
            return 'comfort';
        case 'specialty':
            return 'top-comfort';
    }
}
/**
 * Gera os projetos-exemplo iniciais. Executado apenas quando a IndexedDB
 * está vazia — primeira execução do app no dispositivo.
 */
export function buildSeedProjects() {
    const mem50 = materialRegistry.getMaterial('mat-memory-50');
    const hrCore = materialRegistry.getMaterial('mat-hr-core');
    const hrTransition = materialRegistry.getMaterial('mat-transition-hr');
    const latex = materialRegistry.getMaterial('mat-latex-natural');
    const d33 = materialRegistry.getMaterial('mat-d33-core');
    const soft = materialRegistry.getMaterial('mat-soft-d23');
    const pocket = materialRegistry.getMaterial('mat-pocket-coil');
    const balanceado = createStarterProject(hrCore, hrTransition, mem50, 'fab-knit-soft', 'fab-suede-side', 'Linha Conforto — Casal');
    const premium = createProject({
        nome: 'Linha Premium — Queen',
        preset: 'queen',
        tecidoSuperiorId: 'fab-quilted-top',
        tecidoLateralId: 'fab-suede-side',
        camadasIniciais: reindexLayers([
            createLayer({ material: pocket, role: 'core', ordem: 0 }),
            createLayer({ material: hrTransition, role: 'transition', ordem: 1 }),
            createLayer({ material: latex, role: 'top-comfort', ordem: 2 }),
        ]),
    });
    const essencial = createProject({
        nome: 'Linha Essencial — Solteiro',
        preset: 'solteiro',
        tecidoSuperiorId: 'fab-jacquard',
        tecidoLateralId: 'fab-suede-side',
        camadasIniciais: reindexLayers([
            createLayer({ material: d33, role: 'core', ordem: 0 }),
            createLayer({ material: soft, role: 'top-comfort', ordem: 1 }),
        ]),
    });
    return [touchProject(balanceado), touchProject(premium), touchProject(essencial)];
}
export const useProjectLibrary = create()(immer((set, get) => ({
    hydrationStatus: 'pending',
    projects: {},
    order: [],
    listSummaries() {
        const state = get();
        return state.order
            .map((id) => state.projects[id])
            .filter((p) => Boolean(p))
            .map((p) => ({
            id: p.id,
            nome: p.nome,
            dataAtualizacao: p.dataAtualizacao,
            alturaTotalMm: p.dimensoes.alturaTotalMm,
            larguraMm: p.dimensoes.larguraMm,
            comprimentoMm: p.dimensoes.comprimentoMm,
            numeroDeCamadas: p.listaDeCamadas.length,
            previewImage: p.previewImage,
        }))
            .sort((a, b) => b.dataAtualizacao.localeCompare(a.dataAtualizacao));
    },
    getProject(id) {
        return get().projects[id];
    },
    hydrateFromRepository(projects) {
        set((s) => {
            s.projects = {};
            s.order = [];
            for (const p of projects) {
                s.projects[p.id] = p;
                s.order.push(p.id);
            }
            s.hydrationStatus = 'ready';
        });
    },
    setHydrationStatus(status) {
        set((s) => {
            s.hydrationStatus = status;
        });
    },
    importProject(project) {
        set((s) => {
            s.projects[project.id] = project;
            s.order.unshift(project.id);
        });
    },
    setProjectThumbnail(projectId, dataUrl) {
        set((s) => {
            const p = s.projects[projectId];
            if (!p)
                return;
            p.previewImage = dataUrl;
            // não bumpar dataAtualizacao — thumbnail é side-effect do render,
            // não uma edição do usuário
        });
    },
    createProjectFromProfile(input) {
        const { project } = createProjectFromRecommendation(input);
        set((s) => {
            s.projects[project.id] = project;
            s.order.unshift(project.id);
        });
        return project;
    },
    updateClientProfile(id, patch) {
        set((s) => {
            const p = s.projects[id];
            if (!p)
                return;
            p.perfilCliente = { ...(p.perfilCliente ?? {}), ...patch };
            p.dataAtualizacao = new Date().toISOString();
        });
    },
    createNewProject(nome) {
        const hrCore = materialRegistry.getMaterial('mat-hr-core');
        const transition = materialRegistry.getMaterial('mat-transition-hr');
        const comfort = materialRegistry.getMaterial('mat-memory-50');
        const p = createStarterProject(hrCore, transition, comfort, 'fab-knit-soft', 'fab-suede-side', nome || 'Novo colchão');
        set((s) => {
            s.projects[p.id] = p;
            s.order.unshift(p.id);
        });
        return p;
    },
    duplicateProject(id) {
        const original = get().projects[id];
        if (!original)
            return undefined;
        const cloneLayers = original.listaDeCamadas.map((l) => ({ ...l, id: crypto.randomUUID() }));
        const clone = touchProject({
            ...original,
            id: crypto.randomUUID(),
            nome: `${original.nome} (cópia)`,
            dataCriacao: new Date().toISOString(),
            listaDeCamadas: cloneLayers,
            dimensoes: {
                ...original.dimensoes,
                alturaTotalMm: computeTotalHeightMm(cloneLayers),
            },
        });
        set((s) => {
            s.projects[clone.id] = clone;
            s.order.unshift(clone.id);
        });
        return clone;
    },
    deleteProject(id) {
        set((s) => {
            delete s.projects[id];
            s.order = s.order.filter((x) => x !== id);
        });
    },
    renameProject(id, nome) {
        set((s) => {
            const p = s.projects[id];
            if (!p)
                return;
            p.nome = nome;
            p.dataAtualizacao = new Date().toISOString();
        });
    },
    updateProject(project) {
        set((s) => {
            s.projects[project.id] = touchProject(project);
        });
    },
    // ─── Layer mutations ───────────────────────────────────
    addLayer(projectId, materialId, roleOverride) {
        const material = materialRegistry.getMaterial(materialId);
        if (!material)
            return undefined;
        let newId;
        set((s) => {
            const p = s.projects[projectId];
            if (!p)
                return;
            const role = roleOverride ?? defaultRoleForCategory(material.categoria);
            const layer = createLayer({
                material,
                role,
                ordem: p.listaDeCamadas.length,
            });
            newId = layer.id;
            p.listaDeCamadas.push(layer);
            p.listaDeCamadas = reindexLayers(p.listaDeCamadas);
            p.dimensoes.alturaTotalMm = computeTotalHeightMm(p.listaDeCamadas);
            p.dataAtualizacao = new Date().toISOString();
        });
        return newId;
    },
    removeLayer(projectId, layerId) {
        set((s) => {
            const p = s.projects[projectId];
            if (!p)
                return;
            p.listaDeCamadas = reindexLayers(p.listaDeCamadas.filter((l) => l.id !== layerId));
            p.dimensoes.alturaTotalMm = computeTotalHeightMm(p.listaDeCamadas);
            p.dataAtualizacao = new Date().toISOString();
        });
    },
    updateLayer(projectId, layerId, patch) {
        set((s) => {
            const p = s.projects[projectId];
            if (!p)
                return;
            const idx = p.listaDeCamadas.findIndex((l) => l.id === layerId);
            if (idx < 0)
                return;
            const current = p.listaDeCamadas[idx];
            p.listaDeCamadas[idx] = { ...current, ...patch, id: current.id };
            p.dimensoes.alturaTotalMm = computeTotalHeightMm(p.listaDeCamadas);
            p.dataAtualizacao = new Date().toISOString();
        });
    },
    setLayerThickness(projectId, layerId, espessuraMm) {
        set((s) => {
            const p = s.projects[projectId];
            if (!p)
                return;
            const layer = p.listaDeCamadas.find((l) => l.id === layerId);
            if (!layer)
                return;
            const material = materialRegistry.getMaterial(layer.materialId);
            const min = material?.alturaMinMm ?? 10;
            const max = material?.alturaMaxMm ?? 200;
            layer.espessuraMm = Math.max(min, Math.min(max, Math.round(espessuraMm)));
            p.dimensoes.alturaTotalMm = computeTotalHeightMm(p.listaDeCamadas);
            p.dataAtualizacao = new Date().toISOString();
        });
    },
    toggleLayerVisibility(projectId, layerId) {
        set((s) => {
            const p = s.projects[projectId];
            if (!p)
                return;
            const layer = p.listaDeCamadas.find((l) => l.id === layerId);
            if (!layer)
                return;
            layer.visible = !layer.visible;
            p.dimensoes.alturaTotalMm = computeTotalHeightMm(p.listaDeCamadas);
            p.dataAtualizacao = new Date().toISOString();
        });
    },
    toggleLayerLock(projectId, layerId) {
        set((s) => {
            const p = s.projects[projectId];
            if (!p)
                return;
            const layer = p.listaDeCamadas.find((l) => l.id === layerId);
            if (!layer)
                return;
            layer.locked = !layer.locked;
        });
    },
    renameLayer(projectId, layerId, nome) {
        set((s) => {
            const p = s.projects[projectId];
            if (!p)
                return;
            const layer = p.listaDeCamadas.find((l) => l.id === layerId);
            if (!layer)
                return;
            layer.nome = nome.trim() || layer.nome;
            p.dataAtualizacao = new Date().toISOString();
        });
    },
    setLayerOrder(projectId, orderedIds) {
        set((s) => {
            const p = s.projects[projectId];
            if (!p)
                return;
            const map = new Map(p.listaDeCamadas.map((l) => [l.id, l]));
            const reordered = [];
            for (const id of orderedIds) {
                const layer = map.get(id);
                if (layer)
                    reordered.push(layer);
            }
            if (reordered.length !== p.listaDeCamadas.length)
                return;
            p.listaDeCamadas = reindexLayers(reordered);
            p.dataAtualizacao = new Date().toISOString();
        });
    },
    setDimensionPreset(projectId, preset) {
        const dim = DIMENSION_PRESETS[preset];
        if (!dim)
            return;
        set((s) => {
            const p = s.projects[projectId];
            if (!p)
                return;
            p.dimensoes.larguraMm = dim.larguraMm;
            p.dimensoes.comprimentoMm = dim.comprimentoMm;
            p.dataAtualizacao = new Date().toISOString();
        });
    },
    setCustomDimensions(projectId, dims) {
        set((s) => {
            const p = s.projects[projectId];
            if (!p)
                return;
            if (dims.larguraMm !== undefined) {
                p.dimensoes.larguraMm = Math.max(400, Math.min(2400, Math.round(dims.larguraMm)));
            }
            if (dims.comprimentoMm !== undefined) {
                p.dimensoes.comprimentoMm = Math.max(800, Math.min(2400, Math.round(dims.comprimentoMm)));
            }
            p.dataAtualizacao = new Date().toISOString();
        });
    },
    updateAcabamento(projectId, patch) {
        set((s) => {
            const p = s.projects[projectId];
            if (!p)
                return;
            p.acabamento = { ...p.acabamento, ...patch };
            p.dataAtualizacao = new Date().toISOString();
        });
    },
    setTecidos(projectId, topId, sideId) {
        set((s) => {
            const p = s.projects[projectId];
            if (!p)
                return;
            if (topId)
                p.tecidoSuperiorId = topId;
            if (sideId)
                p.tecidoLateralId = sideId;
            p.dataAtualizacao = new Date().toISOString();
        });
    },
})));
